import { useLocation, useNavigate } from "react-router-dom";

import { InvoiceAddEditButtons } from "./InvoiceAddEditButtons";
import { InvoiceDownloadButton } from "./InvoiceDownloadButton";

import { MonthYearSelector } from "@/components/ui/v2/month-year-selector";
import { TableToolbar } from "@/components/ui/v2/table/table-toolbar";
import { ROUTES } from "@/constants/routes";

type InvoiceIntegrationModuleTableToolbarProps = {
  onSearchChange: (value: string) => void;
  onDownload: () => void;
  searchValue?: string;
  year: string;
  month: string;
  tenantIntegrationModuleId: string;
  maxDate?: [string, string];
  currentPageLabel?: string;
  integrationModuleName?: string;
  isEditMode: boolean;
  onEditModeChange: (isEditMode: boolean) => void;
  onSave: () => void;
  onCancel: () => void;
  isEditDisabled?: boolean;
  isSaving?: boolean;
  hasData?: boolean;
  isDownloading?: boolean;
};

export function InvoiceIntegrationModuleTableToolbar({
  onSearchChange,
  onDownload,
  searchValue,
  year,
  month,
  tenantIntegrationModuleId,
  maxDate,
  currentPageLabel,
  integrationModuleName,
  isEditMode,
  onEditModeChange,
  onSave,
  onCancel,
  isEditDisabled = false,
  isSaving = false,
  hasData = false,
  isDownloading = false,
}: InvoiceIntegrationModuleTableToolbarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const getSelectedTabId = () => {
    const currentPath = location.pathname;
    if (currentPath.includes(`/${ROUTES.toDo}`)) {
      return "toDo";
    }
    if (currentPath.includes(`/${ROUTES.invoiced}`)) {
      return "invoiced";
    }
    return "toDo";
  };

  const handleYearMonthChange = (yearMonth: [string, string]) => {
    const [newYear, newMonth] = yearMonth;
    const currentTab = getSelectedTabId() || "toDo";
    const tabRoute =
      currentTab === "toDo" ? ROUTES.toDo : ROUTES.invoiced;
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("page");
    const cleanSearch = searchParams.toString();
    const searchString = cleanSearch ? `?${cleanSearch}` : "";
    navigate(
      `/${ROUTES.invoices}/${ROUTES.integrations}/${tenantIntegrationModuleId}/${tabRoute}/${newYear}/${newMonth}${searchString}`,
      {
        state: {
          integrationModuleName: currentPageLabel ?? integrationModuleName,
        },
      }
    );
  };

  const currentTab = getSelectedTabId();

  return (
    <div className="flex flex-col w-full gap-4">
      <div className="flex justify-end">
        <div className="flex items-center gap-2">
          <InvoiceDownloadButton
            onDownload={onDownload}
            isLoading={isDownloading}
            hasData={hasData}
          />
          <InvoiceAddEditButtons
            isEditMode={isEditMode}
            onEditModeChange={onEditModeChange}
            onSave={onSave}
            onCancel={onCancel}
            isEditDisabled={isEditDisabled}
            isSaving={isSaving}
            hasData={hasData}
            tabType={currentTab}
          />
        </div>
      </div>
      <div className="flex items-center gap-2 justify-between">
        <TableToolbar
          onSearchChange={onSearchChange}
          searchValue={searchValue}
        />
        <MonthYearSelector
          value={[year, month]}
          onChange={handleYearMonthChange}
          maxDate={maxDate}
        />
      </div>
    </div>
  );
}
